// The Vehicle Assets view's selection lives in the URL, not in component state.
//
// WHY: clicking a car navigates to /photographer/vehicle/..., which UNMOUNTS
// PhotographerInventory and throws away every useState in it. "Back to inventory" then
// mounted a fresh component with mode='dealer' and photographerId=null, so an admin was
// forced to re-pick the photographer after looking at a single car (Kim, 2026-10-08). The
// same reset hit the dealership, search, filter and sort on the browse-by-dealer path.
//
// Reading the selection from the URL fixes it without any cross-screen state, and it gives
// Kim's own exception for free: returning to a BARE /photographer starts clean, so "go to
// the home screen and pick a different photographer" keeps working exactly as it reads.
// It also survives a refresh and makes a view linkable, which sessionStorage would not.
//
// Only non-default values are written, so the common case stays a clean URL.

export const INVENTORY_DEFAULTS = {
  mode: 'dealer',
  photographerId: null,
  dealerId: null,
  search: '',
  filter: '',
  sort: 'default',
};

// `mine` carries the non-admin "show only my shots" checkbox, which has no id to key on.
// Without it, a photographer's own toggle would be indistinguishable from the default.
export function readInventoryQuery(searchParams) {
  const get = (k) => {
    const v = searchParams && searchParams.get ? searchParams.get(k) : null;
    return v === null || v === '' ? null : v;
  };
  const pg = get('pg');
  const pgNum = pg !== null && /^\d+$/.test(pg) ? Number(pg) : null;
  const d = get('d');
  const dNum = d !== null && /^\d+$/.test(d) ? Number(d) : null;
  const mine = get('mine') === '1';
  const filter = get('f');
  const sort = get('s');
  return {
    // An id always wins; `mine` covers the id-less photographer case.
    mode: pgNum !== null || mine ? 'photographer' : 'dealer',
    photographerId: pgNum,
    dealerId: dNum,
    search: get('q') || '',
    // Reject anything not in the known set rather than passing a hand-typed value
    // straight into the API call.
    filter: ['needs_photos', 'complete', 'in_progress'].includes(filter) ? filter : '',
    sort: ['photos', 'year'].includes(sort) ? sort : 'default',
  };
}

export function buildInventoryQuery(state) {
  const s = state || {};
  const p = new URLSearchParams();
  if (s.mode === 'photographer') {
    if (s.photographerId) p.set('pg', String(s.photographerId));
    else p.set('mine', '1');
  } else if (s.dealerId) {
    p.set('d', String(s.dealerId));
  }
  if (s.search && s.search.trim()) p.set('q', s.search);
  // Filter and sort only exist in the dealer-browse controls; carrying them in
  // photographer mode would restore a control the page does not render.
  if (s.mode !== 'photographer') {
    if (s.filter) p.set('f', s.filter);
    if (s.sort && s.sort !== 'default') p.set('s', s.sort);
  }
  return p.toString();
}
